import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { buildLoginPath, consumeExplicitLogout } from "@/lib/loginRedirect";
import { getOrderStatusClass, getOrderStatusLabel } from "@/lib/orderStatus";
import { VENDOR_PRODUCT_OPTIONS } from "@/lib/productOptions";
import { BadgeCheck, Building2, CheckCircle2, Hash, Mail, Package, Phone, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";

function displayEmail(email) {
  const value = String(email || "").trim();
  if (!value || value.endsWith("@railaxis.local")) return "Not provided";
  return value;
}

function roleLabel(role) {
  if (role === "vendor") return "Vendor";
  if (role === "contractor") return "Contractor";
  if (role === "admin") return "Admin";
  return "User";
}

function vendorProductLabel(slug) {
  const value = String(slug || "").trim();
  if (!value) return "Not assigned";
  const option = VENDOR_PRODUCT_OPTIONS.find((item) => (
    item.slug.toLowerCase() === value.toLowerCase() || item.routeSlug?.toLowerCase() === value.toLowerCase()
  ));
  return option?.label || value;
}

function DetailItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#4B5563]">
        <Icon className="h-4 w-4 text-[#C8A96A]" />
        {label}
      </div>
      <div className="mt-2 break-words text-sm font-semibold text-[#111827]">{value || "-"}</div>
    </div>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatQuantity(item) {
  return `${Number(item?.quantity || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })} ${item?.unit || ""}`.trim();
}

export default function Profile() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      if (consumeExplicitLogout()) {
        navigate("/", { replace: true });
        return;
      }
      navigate(buildLoginPath("/profile"), { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role !== "contractor") return;
    setOrdersLoading(true);
    api.get("/orders")
      .then(({ data }) => setOrders(data || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setOrdersLoading(false));
  }, [user?.id, user?.role]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="py-32 text-center text-[#4B5563]">Loading profile...</div>
      </div>
    );
  }

  const isVendor = user.role === "vendor";
  const isContractor = user.role === "contractor";
  const initials = (user.name || user.company || "U").trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 py-12 md:px-10 md:py-16" data-testid="profile-page">
        <div className="mb-8">
          <div className="section-label">Profile</div>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-[#111827] md:text-4xl">
            Account Details
          </h1>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-5 border-b border-gray-100 bg-[#FDFBF7] px-5 py-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#C8A96A] text-2xl font-bold text-white shadow-sm">
                {initials}
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-[#111827]">{user.name || "User"}</h2>
                <p className="mt-1 text-sm text-[#4B5563]">{user.company || "Company not provided"}</p>
              </div>
            </div>
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#C8A96A]/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#111827]">
              <ShieldCheck className="h-4 w-4 text-[#C8A96A]" />
              {roleLabel(user.role)}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
            <DetailItem icon={User} label="Name" value={user.name || "Not provided"} />
            <DetailItem icon={Building2} label="Company" value={user.company || "Not provided"} />
            <DetailItem icon={Phone} label="Phone" value={user.phone || "Not provided"} />
            <DetailItem icon={Mail} label="Email" value={displayEmail(user.email)} />
            <DetailItem icon={Hash} label="Account ID" value={user.id ? user.id.slice(0, 8) : "Not available"} />
            <DetailItem icon={BadgeCheck} label="Account Type" value={roleLabel(user.role)} />

            {isVendor && (
              <>
                <DetailItem icon={Hash} label="Vendor ID" value={user.vendor_code || user.vendor_id || "Not assigned"} />
                <DetailItem icon={BadgeCheck} label="Approval Status" value={user.vendor_approved ? "Approved" : "Pending Approval"} />
                <DetailItem icon={Package} label="Product Category" value={vendorProductLabel(user.vendor_product_slug)} />
                <DetailItem icon={Package} label="Brand" value={user.vendor_brand || "Not assigned"} />
              </>
            )}
          </div>
        </div>

        {isContractor && (
          <div className="mt-8 overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="profile-order-history">
            <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-display text-xl font-bold text-[#111827]">Order History</h2>
                <p className="mt-1 text-sm text-[#4B5563]">Orders placed from this contractor account.</p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/orders")}
                className="w-fit rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-[#111827] hover:bg-gray-50"
              >
                View Orders
              </button>
            </div>

            {ordersLoading ? (
              <div className="px-5 py-8 text-sm text-[#4B5563]">Loading order history...</div>
            ) : orders.length === 0 ? (
              <div className="px-5 py-8 text-sm text-[#4B5563]">No past orders yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {orders.map((order) => (
                  <div key={order.id} className="p-5">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-[#4B5563]">
                          Order #{String(order.id || "").slice(0, 8)}
                        </div>
                        <div className="mt-1 text-sm text-[#111827]">
                          {order.created_at ? new Date(order.created_at).toLocaleString() : "-"}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 md:justify-end">
                        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getOrderStatusClass(order.order_status)}`}>
                          {getOrderStatusLabel(order.order_status)}
                        </span>
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${order.status === "paid" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                          {order.status === "paid" && <CheckCircle2 className="h-3.5 w-3.5" />}
                          Payment: {order.status === "paid" ? "Paid" : "Pending"}
                        </span>
                        <span className="font-display font-bold text-[#C8A96A]">{formatMoney(order.total)}</span>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2 text-sm text-[#4B5563]">
                      {(order.items || []).map((item, idx) => (
                        <div key={item.id || `${order.id}-${idx}`} className="flex flex-col gap-1 rounded-md bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                          <span className="font-semibold text-[#111827]">
                            {item.product_name || "Product"} ({item.size || "N/A"})
                          </span>
                          <span>
                            {item.brand || "N/A"} · {formatQuantity(item)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
